// Pure footprint + picker-filter projections (Tranche 2). DOM-free,
// network-free, deterministic - the same purity contract as journey.ts.
//
// FOOTPRINTS, NOT COMPLETION: these functions report where the learner has
// already opened sources. They never compute a "percent of the parasha done" -
// the underline fraction is scaled by footprint COUNT with a soft cap, so more
// learning draws a longer line without ever claiming coverage.

import { parseParashaRef } from "./sefaria.ts";

// Is `verseRef` ("Numbers 15:38") a single verse inside the parasha's range
// ("Numbers 13:1-15:41")? Unparseable refs are simply not footprints (false).
export function verseInParasha(verseRef: string, parashaRef: string): boolean {
  let v: ReturnType<typeof parseParashaRef>;
  let p: ReturnType<typeof parseParashaRef>;
  try {
    v = parseParashaRef(verseRef);
    p = parseParashaRef(parashaRef);
  } catch {
    return false;
  }
  if (v.book !== p.book) return false;
  if (v.startCh !== v.endCh || v.startV !== v.endV) return false; // verses only, never ranges
  const afterStart = v.startCh > p.startCh || (v.startCh === p.startCh && v.startV >= p.startV);
  const beforeEnd = v.startCh < p.endCh || (v.startCh === p.endCh && v.startV <= p.endV);
  return afterStart && beforeEnd;
}

// How many of the visited verse refs fall inside this parasha.
export function parashaVisitedCount(parashaRef: string, visited: readonly string[]): number {
  return visited.reduce((n, ref) => n + (verseInParasha(ref, parashaRef) ? 1 : 0), 0);
}

// The picker chip's underline width as a 0..1 fraction: footprint count against
// a soft cap. NOT a completion percentage (we never know the verse total
// offline, and completion language is banned anyway).
const UNDERLINE_CAP = 40;
export function visitedUnderlineFraction(count: number): number {
  if (count <= 0) return 0;
  return Math.min(count, UNDERLINE_CAP) / UNDERLINE_CAP;
}

// Picker typeahead: case-insensitive substring on the English name, plain
// substring on the Hebrew (names are stored without nikud). Empty query
// matches everything.
export function matchesParasha(query: string, en: string, he: string): boolean {
  const q = query.trim();
  if (!q) return true;
  return en.toLowerCase().includes(q.toLowerCase()) || he.includes(q);
}
